import { cx, useTranslations } from "@jugl-web/utils";
import {
  DPConfig,
  DPDatesConfig,
  useDatePickerContext,
} from "@rehookify/datepicker";
import {
  add,
  differenceInMonths,
  format,
  isMonday,
  isSunday,
  sub,
} from "date-fns";
import startOfDay from "date-fns/startOfDay";
import { FC, useMemo } from "react";
import { Button } from "../../../Button";
import { InteractiveContainer } from "../../../InteractiveContainer";
import { PlainButton } from "../../../PlainButton";
import { ReactComponent as ChevronLeftIcon } from "../../assets/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "../../assets/chevron-right.svg";
import { PickerBaseProps } from "../../types";
import { TimePicker } from "../TimePicker";

interface DayPickerProps extends PickerBaseProps {
  initialDate?: Date | Date[];
  isDateRemovable?: boolean;
  removeDateLabel?: string;
  maxDate?: Date;
  minDate?: Date;
  showTimePicker?: boolean;
  mode?: DPDatesConfig["mode"];
  maxRangeInMonths?: number;
  showCancelButton?: boolean;
  allowEmptyDate?: boolean;
  onDatesChange: DPConfig["onDatesChange"];
  onSubmit?: (date: Date | null, date2?: Date | null) => void;
  onDaySelect?: (date: Date) => void;
  onClose?: () => void;
}

export const DayPicker: FC<DayPickerProps> = ({
  initialDate,
  isDateRemovable,
  removeDateLabel,
  maxDate,
  minDate,
  showTimePicker,
  showCancelButton,
  maxRangeInMonths,
  allowEmptyDate,
  onViewChange,
  onDatesChange,
  mode,
  onSubmit,
  onDaySelect,
  onClose,
}) => {
  const {
    data: { calendars, weekDays, selectedDates, months },
    propGetters: { subtractOffset, addOffset, dayButton, setOffset },
  } = useDatePickerContext();

  const { month, year, days } = calendars[0];

  const { t } = useTranslations();

  // Workaround prop getters to fix the issue with min/max date and moving between months
  const getPreviousMonthButtonProps = () => {
    const startOfCurrentMonth = months.find((m) => m.active)?.$date;

    if (!startOfCurrentMonth) {
      return subtractOffset({ months: 1 });
    }

    const endOfPreviousMonth = sub(startOfCurrentMonth, { seconds: 1 });

    return setOffset(endOfPreviousMonth);
  };

  const getNextMonthButtonProps = () => {
    const startOfCurrentMonth = months.find((m) => m.active)?.$date;

    if (!startOfCurrentMonth) {
      return addOffset({ months: 1 });
    }

    const startOfNextMonth = add(startOfCurrentMonth, { months: 1 });

    return setOffset(startOfNextMonth);
  };

  const isDateSelected = selectedDates.length > 0;

  const isSubmitButtonDisabled = useMemo(() => {
    if (allowEmptyDate) {
      return false;
    }

    if (!isDateSelected) {
      return true;
    }

    const [selectedDate, selectedDate2] = selectedDates;

    if (mode === "range" && maxRangeInMonths) {
      const isRangeExceeded =
        differenceInMonths(selectedDate2, selectedDate) > maxRangeInMonths;
      return isRangeExceeded;
    }
    const isBeforeMinDate =
      minDate && startOfDay(selectedDate) < startOfDay(minDate);
    const isAfterMaxDate =
      maxDate && startOfDay(selectedDate) > startOfDay(maxDate);

    return isBeforeMinDate || isAfterMaxDate;
  }, [
    isDateSelected,
    maxDate,
    maxRangeInMonths,
    minDate,
    mode,
    selectedDates,
    allowEmptyDate,
  ]);

  return (
    <>
      <div className="flex h-10 w-full items-center justify-between">
        <PlainButton
          {...getPreviousMonthButtonProps()}
          className="h-10 w-10 rounded-full transition-colors hover:bg-grey-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
        >
          <ChevronLeftIcon />
        </PlainButton>
        <div className="flex items-center justify-center gap-4">
          <InteractiveContainer
            className="flex h-10 items-center rounded-lg bg-grey-100 px-3 font-medium text-dark-800 transition-colors hover:bg-grey-200"
            onClick={() => onViewChange("monthPicker")}
          >
            {month}
          </InteractiveContainer>
          <InteractiveContainer
            className="flex h-10 items-center rounded-lg bg-grey-100 px-3 font-medium text-dark-800 transition-colors hover:bg-grey-200"
            onClick={() => onViewChange("yearPicker")}
          >
            {year}
          </InteractiveContainer>
        </div>
        <PlainButton
          {...getNextMonthButtonProps()}
          className="h-10 w-10 rounded-full transition-colors hover:bg-grey-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
        >
          <ChevronRightIcon />
        </PlainButton>
      </div>
      <div className="mt-8 grid grid-cols-7">
        {weekDays.map((day) => (
          <span
            key={`${month}-${day}`}
            className="text-center text-xs leading-[16px]"
            style={{ color: "rgba(60, 60, 67, 0.60)" }}
          >
            {day}
          </span>
        ))}
      </div>
      <div className="mt-1.5 grid grid-cols-7 gap-1">
        {days.map((dpDay) => {
          const { onClick, ...buttonProps } = dayButton(dpDay);
          const isInRange = dpDay.range === "in-range";
          return (
            <button
              key={dpDay.$date.toDateString()}
              type="button"
              className={cx(
                "flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-none text-sm transition-colors",
                "relative bg-transparent text-black hover:bg-grey-100",
                !dpDay.inCurrentMonth && "text-grey-500",
                dpDay.disabled &&
                  "cursor-not-allowed bg-transparent text-grey-500 hover:bg-transparent",
                dpDay.range === "in-range" &&
                  "rounded-none bg-primary-50 text-black hover:bg-primary-50 hover:text-dark-600",
                dpDay.now &&
                  "bg-primary-50 font-semibold text-primary hover:bg-primary-50",
                dpDay.selected && "font-semibold",
                dpDay.range === "range-start" && "font-semibold",
                dpDay.range === "range-end" && "font-semibold",
                isInRange &&
                  isMonday(dpDay.$date) &&
                  !dpDay.selected &&
                  "rounded-tl-[24px] rounded-tr-none rounded-br-none rounded-bl-[24px]",
                isInRange &&
                  isSunday(dpDay.$date) &&
                  !dpDay.selected &&
                  "rounded-tl-none rounded-tr-[24px] rounded-br-[24px] rounded-bl-none"
              )}
              onClick={(event) => {
                onClick?.(event);
                onDaySelect?.(dpDay.$date);
              }}
              {...buttonProps}
            >
              {isInRange && (
                <div
                  className={cx({
                    "absolute top-0 bottom-0 left-[-10px] h-full w-[12px] bg-primary-50":
                      !isMonday(dpDay.$date),
                  })}
                />
              )}
              {dpDay.range === "range-start" && (
                <div
                  className={cx(
                    !isSunday(dpDay.$date) &&
                      "absolute top-0 right-[-10px] bottom-0 h-full w-[30px] bg-primary-50"
                  )}
                />
              )}
              {dpDay.range === "range-end" && (
                <div
                  className={cx(
                    !isMonday(dpDay.$date) &&
                      "absolute top-0 bottom-0 left-[-10px] h-full w-[30px] bg-primary-50"
                  )}
                />
              )}
              {dpDay.selected && (
                <div
                  className={cx(
                    "absolute top-0 bottom-0 z-10 flex h-full w-full items-center justify-center rounded-full bg-primary text-white"
                  )}
                >
                  {dpDay.day}
                </div>
              )}
              {mode !== "range" && !dpDay.selected && dpDay.day}
              {mode === "range" && !dpDay.selected && dpDay.day}
            </button>
          );
        })}
      </div>
      {showTimePicker && (
        <TimePicker
          className="mt-6"
          minDate={minDate}
          maxDate={maxDate}
          onDatesChange={onDatesChange}
        />
      )}
      {onSubmit && (
        <div className="mt-8 flex flex-col gap-4">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            isDisabled={isSubmitButtonDisabled}
            onClick={() => {
              onSubmit?.(selectedDates[0], selectedDates[1]);
              onClose?.();
            }}
          >
            {mode !== "range" &&
              t({ id: "common.select", defaultMessage: "Select" })}
            {mode === "range" &&
              `${t({
                id: "common.apply",
                defaultMessage: "Apply",
              })} ${selectedDates[0] ? format(selectedDates[0], "MMM d") : ""}${
                selectedDates[1]
                  ? ` - ${format(selectedDates[1], "MMM d")}`
                  : ""
              }`}
          </Button>
          {showCancelButton && (
            <Button
              variant="contained"
              color="grey"
              fullWidth
              onClick={() => {
                onClose?.();
              }}
            >
              {t({ id: "common.cancel", defaultMessage: "Cancel" })}
            </Button>
          )}
          {initialDate && isDateRemovable && (
            <Button
              variant="contained"
              color="grey"
              className="text-tertiary-900"
              fullWidth
              onClick={() => {
                onSubmit?.(null);
                onClose?.();
              }}
            >
              {removeDateLabel ||
                t({
                  id: "date-picker-component.remove-date",
                  defaultMessage: "Remove date",
                })}
            </Button>
          )}
        </div>
      )}
    </>
  );
};
